/**
 * Componente de Inscripción en Jornadas
 * 
 * Permite a los tutores inscribir a sus futbolistas en paquetes de jornadas
 * Muestra información detallada de torneos, sedes y disponibilidad
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import paqueteJornadasService from '../services/paqueteJornadasService';
import futbolistaService from '../services/futbolistaService';
import EquipacionService from '../services/equipacionService';
import SeguroService from '../services/seguroService';
import purchaseService from '../services/purchaseService';
import productService from '../services/productService';
import { toast } from 'react-toastify';
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaEuroSign,
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUsers,
  FaTrophy,
  FaShoppingCart,
  FaInfoCircle
} from 'react-icons/fa';
import useWindowSize from '../hooks/useWindowSize';
// import MobileOptimizedButton from './MobileOptimizedButton';
import '../styles/inscripcionJornadas.css';
import { formatFechaJornada, formatFechaJornadaCorta } from '../utils/fechaUtils';

const InscripcionJornadas = () => {
  const navigate = useNavigate();
  const { isMobile } = useWindowSize();

  // Estados principales
  const [paquetes, setPaquetes] = useState([]); // Todos los paquetes sin filtrar
  const [torneos, setTorneos] = useState([]);
  const [futbolistas, setFutbolistas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paquetesNuevosCompradores, setPaquetesNuevosCompradores] = useState([]); // IDs de paquetes solo para nuevos

  // Estados para filtros
  const [selectedTorneo, setSelectedTorneo] = useState('');
  const [selectedSede, setSelectedSede] = useState('');
  const [selectedFutbolista, setSelectedFutbolista] = useState('');
  const [sedePreferidaId, setSedePreferidaId] = useState(null);

  // Estados para inscripción
  const [inscribiendo, setInscribiendo] = useState(false);
  const [paqueteSeleccionado, setPaqueteSeleccionado] = useState(null);

  // Estado para selección manual de jornadas (por paquete)
  // Formato: { [paqueteId]: [jornadaId1, jornadaId2, ...] }
  const [jornadasSeleccionadas, setJornadasSeleccionadas] = useState({});
  // Controla si el usuario quiere seleccionar manualmente o dejar automático
  // Formato: { [paqueteId]: true/false }
  const [seleccionManual, setSeleccionManual] = useState({});

  // Estados para equipación
  const [incluirEquipacion, setIncluirEquipacion] = useState({});
  const [tallaEquipacion, setTallaEquipacion] = useState({});
  const [equipacionInfo, setEquipacionInfo] = useState(null);
  const [tallasDisponibles, setTallasDisponibles] = useState([]);
  const [mostrarGuiaTallas, setMostrarGuiaTallas] = useState(false);

  // Estado para verificar si cada futbolista es nuevo (sin compras previas)
  // Formato: { futbolistaId: boolean }
  const [futbolistasNuevos, setFutbolistasNuevos] = useState({});

  // IDs de jornadas donde el futbolista ya está inscrito (para deshabilitar en selector)
  const [jornadasYaInscritas, setJornadasYaInscritas] = useState([]);

  // Estados para seguro voluntario de accidentes
  const [incluirSeguro, setIncluirSeguro] = useState({});
  const [seguroInfo, setSeguroInfo] = useState(null); // { precio_base, precio_prorrateado, fecha_inicio, fecha_fin }
  const [futbolistasTienenSeguro, setFutbolistasTienenSeguro] = useState({}); // { futbolistaId: boolean }

  // Mover loadPaquetes antes del useEffect que lo usa
  const loadPaquetes = useCallback(async () => {
    try {
      const filters = {};

      if (selectedTorneo) {
        filters.torneo_id = selectedTorneo;
      }

      if (selectedSede) {
        filters.sede_id = selectedSede;
      }

      const paquetesData = await paqueteJornadasService.getPaquetesDisponibles(filters);
      setPaquetes(paquetesData);

      // Para paquetes promocionales con equipación incluida, marcar automáticamente la equipación
      const blackFridayPaquete = paquetesData.find(p => p.id === 9);
      const reyesMagosPaquete = paquetesData.find(p => p.id === 10);
      const semanaSantaPaquete = paquetesData.find(p => p.id === 11);

      if (blackFridayPaquete || reyesMagosPaquete || semanaSantaPaquete) {
        setIncluirEquipacion(prev => ({
          ...prev,
          [9]: true, // Equipación siempre incluida para Black Friday
          [10]: true, // Equipación siempre incluida para Reyes Magos
          [11]: true // Equipación siempre incluida para Semana Santa
        }));
      }

    } catch (error) {
      console.error('Error al cargar paquetes:', error);
      toast.error('Error al cargar los paquetes');
    }
  }, [selectedTorneo, selectedSede]);

  useEffect(() => {
    loadData();
    loadEquipacionInfo();
  }, []);

  useEffect(() => {
    if (!loading && selectedTorneo) {
      loadPaquetes();
    }
  }, [selectedTorneo, selectedSede, loading, loadPaquetes]);

  // Verificar si el futbolista debe comprar equipación según la Sede y el Torneo
  // y obtener jornadas donde ya está inscrito
  useEffect(() => {
    const verificarYMarcarEquipacion = async () => {
      if (selectedFutbolista && paquetes.length > 0) {
        const resultados = await checkEquipacionObligatoria(selectedFutbolista, paquetes);

        // Limpiar equipación y tallas al cambiar de futbolista
        setIncluirEquipacion({});
        setTallaEquipacion({});

        const equipacionPorDefecto = {};

        // El resultado viene indexado por paqueteId
        if (resultados) {
          paquetes.forEach(paquete => {
            // Primera Experiencia: equipación prestada, nunca se marca como "incluir"
            if (paquete.tipo === 'primera_experiencia') return;
            if (resultados[paquete.id]?.obligatoria) {
              equipacionPorDefecto[paquete.id] = true;
            }
          });
          setIncluirEquipacion(equipacionPorDefecto);
        }

        // Obtener jornadas donde el futbolista ya está inscrito (para el torneo seleccionado)
        try {
          const inscritas = await purchaseService.getJornadasInscritas(selectedFutbolista, selectedTorneo || null);
          setJornadasYaInscritas(inscritas);
        } catch (err) {
          console.error('Error al obtener jornadas inscritas:', err);
          setJornadasYaInscritas([]);
        }
      } else {
        setJornadasYaInscritas([]);
      }
    };

    verificarYMarcarEquipacion();
  }, [selectedFutbolista, paquetes, selectedTorneo]);

  // Verificar seguro y cargar precio prorrateado cuando cambia el futbolista
  useEffect(() => {
    const cargarInfoSeguro = async () => {
      if (!selectedFutbolista) {
        setSeguroInfo(null);
        setIncluirSeguro({});
        return;
      }

      try {
        // Obtener precio prorrateado y verificar si ya tiene seguro
        const precioData = await SeguroService.getPrecioSeguro(selectedFutbolista);

        if (precioData.tiene_seguro) {
          // Ya tiene seguro activo - marcar para deshabilitar toggle
          setFutbolistasTienenSeguro(prev => ({ ...prev, [selectedFutbolista]: true }));
          setSeguroInfo(null);
          setIncluirSeguro({});
        } else {
          setFutbolistasTienenSeguro(prev => ({ ...prev, [selectedFutbolista]: false }));
          setSeguroInfo({
            precio_base: precioData.precio_base,
            precio_prorrateado: precioData.precio_prorrateado,
            fecha_inicio: precioData.fecha_inicio_cobertura,
            fecha_fin: precioData.fecha_fin_cobertura
          });
        }
      } catch (error) {
        console.error('Error al cargar info del seguro:', error);
        setSeguroInfo(null);
      }
    };

    cargarInfoSeguro();
  }, [selectedFutbolista]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [torneosData, futbolistasData, paqueteIdsResponse] = await Promise.all([
        paqueteJornadasService.getTorneosActivos(),
        futbolistaService.getFutbolistas(),
        productService.getPaqueteIdsNuevosCompradores()
      ]);

      setTorneos(torneosData);
      setFutbolistas(futbolistasData);
      setPaquetesNuevosCompradores(paqueteIdsResponse.data || []);

      console.log('📦 IDs de paquetes para nuevos compradores:', paqueteIdsResponse.data);

      // Obtener sede preferida del usuario
      let sedePreferida = null;
      try {
        const sedeRes = await api.get('/postgres/jornadas/sede-preferida');
        sedePreferida = sedeRes.data?.data?.sede_preferida;
        if (sedePreferida) {
          setSedePreferidaId(sedePreferida.id.toString());
        }
      } catch (_err) { /* silenciar */ }

      // Seleccionar el primer torneo por defecto
      if (torneosData.length > 0) {
        setSelectedTorneo(torneosData[0].id.toString());
      }

      // Seleccionar el primer futbolista por defecto
      if (futbolistasData.length > 0) {
        setSelectedFutbolista(futbolistasData[0].id.toString());
      }

    } catch (error) {
      console.error('Error al cargar datos:', error);
      setError('Error al cargar los datos. Por favor, inténtalo de nuevo.');
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const loadEquipacionInfo = async () => {
    try {
      const [equipacion, tallas] = await Promise.all([
        EquipacionService.getEquipacionInfoCached(),
        EquipacionService.getTallasDisponibles()
      ]);

      setEquipacionInfo(equipacion);
      setTallasDisponibles(tallas);
    } catch (error) {
      console.error('Error al cargar información de equipación:', error);
      // Usar valores por defecto si falla
      setEquipacionInfo({
        precio: 15.00,
        nombre: 'Equipación Completa JOMA',
        descripcion: 'Incluye Camiseta y Pantalón Oficial de la Liga Prokids'
      });
      setTallasDisponibles(['5XS-6XS', '4XS-3XS', '2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL']);
    }
  };

  /**
   * Verificar la obligatoriedad de la equipación para los paquetes seleccionados
   */
  const checkEquipacionObligatoria = async (futbolistaId, paquetesToCheck) => {
    try {
      console.log(`🔍 Verificando equipación para futbolista ${futbolistaId}...`);
      
      const response = await purchaseService.verificarEquipacionObligatoria(
        futbolistaId, 
        paquetesToCheck.map(p => ({
          id: p.id,
          torneo_id: p.torneo_id,
          sede_id: p.sede_id
        }))
      );
      
      console.log(`📦 Resultados de equipación:`, response.data);
      
      // Guardar el estado de obligatoriedad en el componente para usarlo al validar el formulario
      setFutbolistasNuevos(response.data); // Reutilizamos este estado para guardar el mapa {paqueteId: {obligatoria}}
      
      return response.data;
    } catch (error) {
      console.error(`❌ Error al verificar equipación obligatoria:`, error);
      return null;
    }
  };

  const handleInscripcion = (paquete) => {
    const errorValidacion = validarInscripcion(paquete);
    if (errorValidacion) {
      toast.warning(errorValidacion);
      return;
    }

    try {
      setInscribiendo(true);
      setPaqueteSeleccionado(paquete);

      // Buscar el objeto futbolista completo
      const futbolistaCompleto = futbolistas.find(f => f.id.toString() === selectedFutbolista);

      // Preparar datos de la inscripción
      // Si hay selección manual de jornadas, incluir los IDs seleccionados
      const jornadasManual = seleccionManual[paquete.id] ? jornadasSeleccionadas[paquete.id] : null;

      const datosInscripcion = {
        paquete,
        futbolista_id: selectedFutbolista,
        futbolista: futbolistaCompleto,
        torneo: torneos.find(t => t.id.toString() === selectedTorneo),
        equipacion: {
          incluir: incluirEquipacion[paquete.id] || false,
          talla: tallaEquipacion[paquete.id] || null,
          precio: incluirEquipacion[paquete.id]
            ? Math.max(0, (equipacionInfo?.precio || 15.00) - (futbolistasNuevos?.[paquete.id]?.primera_experiencia_usada ? 3.00 : 0))
            : 0
        },
        seguro: {
          incluir: incluirSeguro[paquete.id] || false,
          precio: incluirSeguro[paquete.id] ? (seguroInfo?.precio_prorrateado || 0) : 0
        },
        precio_total: calcularPrecioTotal(paquete),
        // Jornadas seleccionadas manualmente (null = automático, las próximas N)
        jornadas_seleccionadas: jornadasManual && jornadasManual.length > 0 ? jornadasManual : null
      };

      toast.success('Redirigiendo al proceso de pago...');

      // Redirigir al checkout con los datos necesarios
      navigate(`/products/${paquete.id}/checkout`, {
        state: datosInscripcion
      });

    } catch (error) {
      console.error('Error en inscripción:', error);
      toast.error('Error al procesar la inscripción');
    } finally {
      setInscribiendo(false);
      setPaqueteSeleccionado(null);
    }
  };

  const sedesDisponibles = useMemo(() => {
    const torneoSeleccionado = torneos.find(t => t.id.toString() === selectedTorneo);
    return torneoSeleccionado?.sedesEnTorneo || [];
  }, [torneos, selectedTorneo]);

  // Seleccionar automáticamente la sede preferida del usuario, o la única disponible
  useEffect(() => {
    if (sedesDisponibles.length === 0) {
      setSelectedSede('');
    } else if (sedePreferidaId && sedesDisponibles.some(s => s.sede.id.toString() === sedePreferidaId)) {
      setSelectedSede(sedePreferidaId);
    } else if (sedesDisponibles.length === 1) {
      setSelectedSede(sedesDisponibles[0].sede.id.toString());
    }
  }, [sedesDisponibles, sedePreferidaId]);

  const formatPrice = useCallback((price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  }, []);

  const getDisponibilidadColor = (paquete) => {
    if (!paquete.jornadas_info.fecha_limite_inscripcion) return 'danger';

    const ahora = new Date();
    const fechaLimite = new Date(paquete.jornadas_info.fecha_limite_inscripcion);

    // Siempre permitir compra, pero cambiar color según proximidad a fecha límite
    const diasRestantes = Math.ceil((fechaLimite - ahora) / (1000 * 60 * 60 * 24));
    if (diasRestantes <= 1) return 'warning';
    return 'success';
  };

  const getDisponibilidadTexto = (paquete) => {
    if (!paquete.jornadas_info.fecha_limite_inscripcion) {
      return 'Sin jornadas programadas';
    }

    const ahora = new Date();
    const fechaLimite = new Date(paquete.jornadas_info.fecha_limite_inscripcion);

    if (ahora > fechaLimite) {
      return 'Participación desde próxima jornada disponible';
    }

    const diasRestantes = Math.ceil((fechaLimite - ahora) / (1000 * 60 * 60 * 24));
    const horasRestantes = Math.ceil((fechaLimite - ahora) / (1000 * 60 * 60));

    if (diasRestantes <= 0) {
      return `Quedan ${horasRestantes} horas para próxima jornada`;
    } else if (diasRestantes === 1) {
      return 'Último día para próxima jornada';
    } else {
      return `${diasRestantes} días para próxima jornada`;
    }
  };

  // Funciones para equipación
  const handleEquipacionChange = (paqueteId, incluir) => {
    setIncluirEquipacion(prev => ({
      ...prev,
      [paqueteId]: incluir
    }));

    // Si no incluye equipación, limpiar la talla
    if (!incluir) {
      setTallaEquipacion(prev => ({
        ...prev,
        [paqueteId]: ''
      }));
    }
  };

  const handleTallaChange = (paqueteId, talla) => {
    setTallaEquipacion(prev => ({
      ...prev,
      [paqueteId]: talla
    }));
  };

  // Funciones para seguro
  const handleSeguroChange = (paqueteId, incluir) => {
    setIncluirSeguro(prev => ({
      ...prev,
      [paqueteId]: incluir
    }));
  };

  const calcularPrecioTotal = useCallback((paquete) => {
    // Para paquetes promocionales con equipación incluida en el precio: precio fijo de 28€
    // Black Friday (ID 9), Reyes Magos (ID 10), Semana Santa (ID 11)
    if (paquete.id === 9 || paquete.id === 10 || paquete.id === 11) {
      const precioSeguro = incluirSeguro[paquete.id] ? (seguroInfo?.precio_prorrateado || 0) : 0;
      return 28.00 + precioSeguro;
    }

    // Para paquetes normales: precio base + equipación opcional + seguro opcional
    const precioBase = parseFloat(paquete.precio);
    // Primera Experiencia: equipación prestada, nunca suma al precio
    let precioEquipacion = (paquete.tipo !== 'primera_experiencia' && incluirEquipacion[paquete.id])
      ? (equipacionInfo?.precio || 15.00) : 0;
    // Descuento €3 en equipación para quienes ya usaron Primera Experiencia
    const yaUsoPrimeraExp = futbolistasNuevos?.[paquete.id]?.primera_experiencia_usada === true;
    if (incluirEquipacion[paquete.id] && yaUsoPrimeraExp) {
      precioEquipacion = Math.max(0, precioEquipacion - 3.00);
    }
    const precioSeguro = incluirSeguro[paquete.id] ? (seguroInfo?.precio_prorrateado || 0) : 0;
    return precioBase + precioEquipacion + precioSeguro;
  }, [incluirEquipacion, equipacionInfo, incluirSeguro, seguroInfo, futbolistasNuevos]);

  const tallasDisponiblesCalculadas = useMemo(() => {
    return tallasDisponibles.length > 0 ? tallasDisponibles :
      ['5XS-6XS', '4XS-3XS', '2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL'];
  }, [tallasDisponibles]);

  // Filtrar paquetes según sede seleccionada y si el futbolista es nuevo
  const paquetesFiltrados = useMemo(() => {
    // Primero filtrar por sede (capa de seguridad adicional al filtro de la API)
    const porSede = selectedSede
      ? paquetes.filter(p => p.sede_id?.toString() === selectedSede.toString())
      : paquetes;

    if (!selectedFutbolista || Object.keys(futbolistasNuevos).length === 0 || paquetesNuevosCompradores.length === 0) {
      return porSede;
    }

    // Filtrar además según si el futbolista califica como "nuevo" para cada paquete específico
    return porSede.filter(p => {
      // Primera Experiencia: ocultar si el futbolista ya la usó en este torneo
      if (p.tipo === 'primera_experiencia' && futbolistasNuevos[p.id]?.primera_experiencia_usada === true) {
        return false;
      }
      // Si el paquete está marcado como "solo para nuevos compradores"
      if (paquetesNuevosCompradores.includes(p.id)) {
        // Solo permitir si el sistema indica que la equipación es obligatoria (es nuevo comprador)
        return futbolistasNuevos[p.id]?.obligatoria === true;
      }
      return true;
    });
  }, [paquetes, selectedSede, selectedFutbolista, futbolistasNuevos, paquetesNuevosCompradores]);

  const validarInscripcion = useCallback((paquete) => {
    if (!selectedFutbolista) {
      return 'Selecciona un futbolista';
    }

    if (!selectedSede && sedesDisponibles.length > 1) {
      return 'Selecciona una sede para continuar';
    }

    // Validación: bloquear re-compra de Primera Experiencia si ya fue usada
    if (paquete.tipo === 'primera_experiencia' && futbolistasNuevos?.[paquete.id]?.primera_experiencia_usada === true) {
      return 'Ya disfrutaste de la Primera Experiencia en este torneo. Elige otro paquete para continuar.';
    }

    // Validación 4: Equipación OBLIGATORIA
    // Primera Experiencia: la equipación es prestada, nunca se exige comprarla
    const obligatoriaParaPaquete = paquete.tipo !== 'primera_experiencia'
      && futbolistasNuevos && futbolistasNuevos[paquete.id]?.obligatoria;

    if (obligatoriaParaPaquete) {
      if (!incluirEquipacion[paquete.id]) {
        return 'Debes incluir la equipación oficial, es obligatoria para este torneo y sede.';
      }
      if (!tallaEquipacion[paquete.id]) {
        return 'Debes seleccionar una talla para la equipación obligatoria.';
      }
    }

    if (incluirEquipacion[paquete.id] && !tallaEquipacion[paquete.id]) {
      return 'Selecciona una talla para la equipación';
    }

    if (!paquete.jornadas_info.fecha_limite_inscripcion) {
      return 'Sin jornadas programadas';
    }

    // Validación: Si selección manual activa, debe tener exactamente N jornadas
    if (seleccionManual[paquete.id]) {
      const seleccionadas = jornadasSeleccionadas[paquete.id]?.length || 0;
      if (seleccionadas !== paquete.cantidad_jornadas) {
        return `Selecciona exactamente ${paquete.cantidad_jornadas} jornada${paquete.cantidad_jornadas > 1 ? 's' : ''}`;
      }
    }

    // NOTA: Ya no bloqueamos la compra por fecha límite
    // La fecha límite solo determina desde qué jornada se empieza a participar

    return null;
  }, [selectedFutbolista, selectedSede, sedesDisponibles, incluirEquipacion, tallaEquipacion, futbolistasNuevos, seleccionManual, jornadasSeleccionadas]);

  if (loading) {
    return (
      <div className="inscripcion-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p>Cargando paquetes de jornadas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inscripcion-error">
        <div className="alert alert-danger">
          <h5>Error al cargar los datos</h5>
          <p>{error}</p>
          <button className="btn btn-outline-danger" onClick={loadData}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="inscripcion-jornadas" style={{
      padding: isMobile ? '20px 0' : '30px 0',
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
    }}>
      <div className="container" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? '0 16px' : '0 20px',
      }}>
        {/* Header */}
        <div className="inscripcion-header" style={{
          textAlign: isMobile ? 'center' : 'left',
          marginBottom: isMobile ? '24px' : '30px',
          padding: isMobile ? '20px' : '24px',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}>
          <h1 style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'center' : 'flex-start',
            gap: '12px',
            fontSize: isMobile ? '1.8rem' : '2.2rem',
            fontWeight: '700',
            color: '#061156',
            marginBottom: '12px',
            lineHeight: '1.2',
          }}>
            <FaTrophy style={{
              fontSize: isMobile ? '24px' : '28px',
              color: '#4352A9'
            }} />
            {isMobile ? 'Inscripciones' : 'Inscripción en Jornadas'}
          </h1>
          <p style={{
            fontSize: isMobile ? '16px' : '18px',
            color: '#6c757d',
            margin: '0',
            lineHeight: '1.5',
          }}>
            {isMobile ?
              'Selecciona un paquete para tu futbolista' :
              'Selecciona un paquete de jornadas para inscribir a tu futbolista'
            }
          </p>
        </div>

        {/* Filtros */}
        <div className="inscripcion-filters" style={{
          backgroundColor: '#ffffff',
          padding: isMobile ? '20px' : '24px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          marginBottom: '24px',
        }}>
          <div className="row" style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: isMobile ? '20px' : '24px',
            alignItems: 'end',
          }}>
            <div style={{ width: '100%' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: isMobile ? '16px' : '14px',
                fontWeight: '600',
                color: '#495057',
                marginBottom: '8px',
              }}>
                <FaTrophy style={{
                  fontSize: isMobile ? '16px' : '14px',
                  color: '#4352A9'
                }} />
                Torneo
              </label>
              <select
                style={{
                  width: '100%',
                  padding: isMobile ? '14px 16px' : '10px 12px',
                  fontSize: isMobile ? '16px' : '14px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  color: '#495057',
                  minHeight: isMobile ? '48px' : '40px',
                  cursor: 'pointer',
                  transition: 'border-color 0.3s ease',
                }}
                value={selectedTorneo}
                onChange={(e) => {
                  setSelectedTorneo(e.target.value);
                  setSelectedSede(''); // Resetear sede al cambiar de torneo
                }}
                onFocus={(e) => e.target.style.borderColor = '#4352A9'}
                onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              >
                <option value="">Seleccionar torneo</option>
                {torneos.map(torneo => (
                  <option key={torneo.id} value={torneo.id}>
                    {torneo.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ width: '100%' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: isMobile ? '16px' : '14px',
                fontWeight: '600',
                color: '#495057',
                marginBottom: '8px',
              }}>
                <FaMapMarkerAlt style={{
                  fontSize: isMobile ? '16px' : '14px',
                  color: '#4352A9'
                }} />
                Sede (Opcional)
              </label>
              <select
                style={{
                  width: '100%',
                  padding: isMobile ? '14px 16px' : '10px 12px',
                  fontSize: isMobile ? '16px' : '14px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '8px',
                  backgroundColor: !selectedTorneo ? '#f8f9fa' : '#ffffff',
                  color: !selectedTorneo ? '#6c757d' : '#495057',
                  minHeight: isMobile ? '48px' : '40px',
                  cursor: !selectedTorneo ? 'not-allowed' : 'pointer',
                  transition: 'border-color 0.3s ease',
                }}
                value={selectedSede}
                onChange={(e) => setSelectedSede(e.target.value)}
                disabled={!selectedTorneo}
                onFocus={(e) => !e.target.disabled && (e.target.style.borderColor = '#4352A9')}
                onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              >
                <option value="">Todas las sedes</option>
                {sedesDisponibles.map(sedeEnTorneo => (
                  <option key={sedeEnTorneo.sede.id} value={sedeEnTorneo.sede.id}>
                    {sedeEnTorneo.sede.nombre} - {sedeEnTorneo.sede.poblacion}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ width: '100%' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: isMobile ? '16px' : '14px',
                fontWeight: '600',
                color: '#495057',
                marginBottom: '8px',
              }}>
                <FaUsers style={{
                  fontSize: isMobile ? '16px' : '14px',
                  color: '#4352A9'
                }} />
                Futbolista
              </label>
              <select
                style={{
                  width: '100%',
                  padding: isMobile ? '14px 16px' : '10px 12px',
                  fontSize: isMobile ? '16px' : '14px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  color: '#495057',
                  minHeight: isMobile ? '48px' : '40px',
                  cursor: 'pointer',
                  transition: 'border-color 0.3s ease',
                }}
                value={selectedFutbolista}
                onChange={(e) => setSelectedFutbolista(e.target.value)}
                onFocus={(e) => e.target.style.borderColor = '#4352A9'}
                onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
              >
                <option value="">Seleccionar futbolista</option>
                {futbolistas.map(futbolista => (
                  <option key={futbolista.id} value={futbolista.id}>
                    {futbolista.nombre} {futbolista.apellidos}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Información del torneo seleccionado */}
        {selectedTorneo && (
          <div className="torneo-info" style={{
            backgroundColor: '#e3f2fd',
            border: '1px solid #2196f3',
            borderRadius: '12px',
            padding: isMobile ? '20px' : '24px',
            marginBottom: '24px',
          }}>
            {(() => {
              const torneo = torneos.find(t => t.id.toString() === selectedTorneo);
              return torneo ? (
                <div>
                  <h5 style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: isMobile ? '1.3rem' : '1.4rem',
                    fontWeight: '700',
                    color: '#1976d2',
                    marginBottom: '16px',
                    textAlign: isMobile ? 'center' : 'left',
                  }}>
                    <FaInfoCircle style={{ fontSize: isMobile ? '20px' : '22px' }} />
                    {torneo.nombre}
                  </h5>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                    gap: isMobile ? '16px' : '20px',
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}>
                      <p style={{
                        margin: '0',
                        fontSize: isMobile ? '15px' : '14px',
                        color: '#1565c0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                        <FaCalendarAlt style={{ fontSize: '16px', flexShrink: 0 }} />
                        <strong>Fecha inicio:</strong> {new Date(torneo.fecha_inicio).toLocaleDateString('es-ES')}
                      </p>
                      <p style={{
                        margin: '0',
                        fontSize: isMobile ? '15px' : '14px',
                        color: '#1565c0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                        <FaCalendarAlt style={{ fontSize: '16px', flexShrink: 0 }} />
                        <strong>Fecha fin:</strong> {new Date(torneo.fecha_fin).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}>
                      <p style={{
                        margin: '0',
                        fontSize: isMobile ? '15px' : '14px',
                        color: '#1565c0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                        <FaTrophy style={{ fontSize: '16px', flexShrink: 0 }} />
                        <strong>Total jornadas:</strong> {torneo.jornadas}
                      </p>
                      <p style={{
                        margin: '0',
                        fontSize: isMobile ? '15px' : '14px',
                        color: '#1565c0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                        <FaMapMarkerAlt style={{ fontSize: '16px', flexShrink: 0 }} />
                        <strong>Sedes disponibles:</strong> {torneo.sedesEnTorneo?.length || 0}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null;
            })()}
          </div>
        )}

        {/* Lista de paquetes */}
        <div className="paquetes-grid" style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: isMobile ? '20px' : '24px',
          marginBottom: '40px',
        }}>
          {paquetesFiltrados.length === 0 ? (
            <div className="no-paquetes" style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: isMobile ? '40px 20px' : '60px 40px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '12px',
              color: '#856404',
            }}>
              <FaExclamationTriangle style={{
                fontSize: isMobile ? '32px' : '48px',
                marginBottom: '16px',
                display: 'block',
                margin: '0 auto 16px auto'
              }} />
              <h4 style={{
                fontSize: isMobile ? '1.2rem' : '1.3rem',
                fontWeight: '600',
                marginBottom: '12px',
              }}>No hay paquetes disponibles</h4>
              <p style={{
                fontSize: isMobile ? '15px' : '16px',
                margin: '0',
                lineHeight: '1.5',
              }}>
                {selectedTorneo
                  ? 'No se encontraron paquetes para los filtros seleccionados.'
                  : 'Selecciona un torneo para ver los paquetes disponibles.'
                }
              </p>
            </div>
          ) : (
            paquetesFiltrados.map(paquete => {
              // Primera Experiencia: equipación prestada, nunca es obligatorio comprarla
              const obligatoria = paquete.tipo !== 'primera_experiencia'
                && futbolistasNuevos && futbolistasNuevos[paquete.id]?.obligatoria;
              return (
                <div key={paquete.id} className="paquete-card" style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '16px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e1e5e9',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative',
                }}
                onMouseOver={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                  }
                }}>
                  <div className={`paquete-header ${paquete.id === 9 ? 'black-friday' : paquete.id === 10 ? 'reyes-magos' : ''}`} style={{
                    padding: isMobile ? '20px' : '24px',
                    borderBottom: (paquete.id === 9 || paquete.id === 10) ? 'none' : '1px solid #e1e5e9',
                    backgroundColor: paquete.id === 9 ? '#000000' : paquete.id === 10 ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' : '#f8f9fa',
                    background: paquete.id === 10 ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' : undefined,
                  }}>
                    {paquete.tipo === 'primera_experiencia' && (
                      <div style={{
                        display: 'inline-block',
                        backgroundColor: '#E3FF38',
                        color: '#000',
                        fontFamily: '"Poppins", sans-serif',
                        fontSize: '11px',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        padding: '3px 10px',
                        borderRadius: '2px',
                        marginBottom: '8px',
                      }}>⚡ Primera Experiencia</div>
                    )}
                    <h3 style={{
                      fontSize: isMobile ? '1.3rem' : '1.4rem',
                      fontWeight: '700',
                      color: (paquete.id === 9 || paquete.id === 10) ? '#FFFFFF' : '#061156',
                      marginBottom: '8px',
                      lineHeight: '1.2',
                    }}>{paquete.nombre}</h3>
                    <div className="paquete-precio" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: isMobile ? '1.5rem' : '1.6rem',
                      fontWeight: '700',
                      color: paquete.id === 9 ? '#FFFFFF' : paquete.id === 10 ? '#FFFFFF' : paquete.id === 11 ? '#FFFFFF' : '#28a745',
                      flexWrap: 'wrap',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FaEuroSign style={{ fontSize: isMobile ? '20px' : '22px' }} />
                        {formatPrice(calcularPrecioTotal(paquete))}
                      </div>
                      {/* Para paquetes promocionales con equipación incluida: NO mostrar desglose */}
                      {paquete.tipo !== 'primera_experiencia' && paquete.id !== 9 && paquete.id !== 10 && paquete.id !== 11 && (incluirEquipacion[paquete.id] || incluirSeguro[paquete.id]) && (() => {
                        const yaUsoPE = futbolistasNuevos?.[paquete.id]?.primera_experiencia_usada === true;
                        const precioEq = equipacionInfo?.precio || 15.00;
                        const precioEqConDesc = incluirEquipacion[paquete.id] ? Math.max(0, precioEq - (yaUsoPE ? 3 : 0)) : 0;
                        return (
                          <div className="precio-desglose" style={{
                            fontSize: isMobile ? '12px' : '13px',
                            color: '#6c757d',
                            fontWeight: '500',
                            width: '100%',
                            marginTop: '4px',
                          }}>
                            <small>
                              Paquete: {formatPrice(paquete.precio)}
                              {incluirEquipacion[paquete.id] && !yaUsoPE && ` + Equipación: ${formatPrice(precioEq)}`}
                              {incluirEquipacion[paquete.id] && yaUsoPE && (
                                <> + Equipación: <span style={{ textDecoration: 'line-through', color: '#aaa' }}>{formatPrice(precioEq)}</span> {formatPrice(precioEqConDesc)} <span style={{ color: '#28a745', fontWeight: '700' }}>(-3€ desc. Primera Experiencia)</span></>
                              )}
                              {incluirSeguro[paquete.id] && ` + Seguro: ${formatPrice(seguroInfo?.precio_prorrateado || 0)}`}
                            </small>
                          </div>
                        );
                      })()}
                      {/* Desglose para paquetes promo con seguro */}
                      {(paquete.id === 9 || paquete.id === 10 || paquete.id === 11) && incluirSeguro[paquete.id] && (
                        <div className="precio-desglose" style={{
                          fontSize: isMobile ? '12px' : '13px',
                          color: paquete.id === 9 || paquete.id === 10 || paquete.id === 11 ? '#ffffffcc' : '#6c757d',
                          fontWeight: '500',
                          width: '100%',
                          marginTop: '4px',
                        }}>
                          <small>
                            Incluye Seguro: +{formatPrice(seguroInfo?.precio_prorrateado || 0)}
                          </small>
                        </div>
                      )}
                      {/* Para paquetes Black Friday y Reyes Magos: mostrar descripción simple en blanco */}
                      {(paquete.id === 9 || paquete.id === 10) && (
                        <div className="precio-descripcion" style={{
                          fontSize: isMobile ? '12px' : '13px',
                          color: '#FFFFFF',
                          fontWeight: '500',
                          width: '100%',
                          marginTop: '4px',
                        }}>
                          <small>
                            2 Jornadas + Equipación Completa
                          </small>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="paquete-body" style={{
                    padding: isMobile ? '20px' : '24px',
                  }}>
                    <p className="paquete-descripcion" style={{
                      fontSize: isMobile ? '14px' : '15px',
                      color: '#6c757d',
                      lineHeight: '1.5',
                      marginBottom: '16px',
                    }}>{paquete.descripcion}</p>

                    <div className="paquete-info" style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      marginBottom: '20px',
                    }}>
                      <div className="info-item" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: isMobile ? '14px' : '15px',
                        color: '#495057',
                      }}>
                        <FaMapMarkerAlt style={{
                          fontSize: '16px',
                          color: '#4352A9',
                          flexShrink: 0
                        }} />
                        <span>{paquete.sede.nombre}</span>
                      </div>
                      <div className="info-item" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: isMobile ? '14px' : '15px',
                        color: '#495057',
                      }}>
                        <FaCalendarAlt style={{
                          fontSize: '16px',
                          color: '#28a745',
                          flexShrink: 0
                        }} />
                        <span>{paquete.cantidad_jornadas} jornadas</span>
                      </div>
                      <div className="info-item" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: isMobile ? '14px' : '15px',
                        color: '#495057',
                      }}>
                        <FaClock style={{
                          fontSize: '16px',
                          color: '#17a2b8',
                          flexShrink: 0
                        }} />
                        <span>{paquete.jornadas_info.proximas} por disputar</span>
                      </div>
                    </div>

                    <div className={`disponibilidad disponibilidad-${getDisponibilidadColor(paquete)}`} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: isMobile ? '12px 16px' : '10px 14px',
                      borderRadius: '8px',
                      fontSize: isMobile ? '14px' : '13px',
                      fontWeight: '600',
                      marginBottom: '16px',
                      backgroundColor: getDisponibilidadColor(paquete) === 'success' ? '#d4edda' : '#fff3cd',
                      color: getDisponibilidadColor(paquete) === 'success' ? '#155724' : '#856404',
                      border: `1px solid ${getDisponibilidadColor(paquete) === 'success' ? '#c3e6cb' : '#ffeaa7'}`,
                    }}>
                      {getDisponibilidadColor(paquete) === 'success' ? (
                        <FaCheckCircle style={{ fontSize: '16px', flexShrink: 0 }} />
                      ) : (
                        <FaExclamationTriangle style={{ fontSize: '16px', flexShrink: 0 }} />
                      )}
                      <span>{getDisponibilidadTexto(paquete)}</span>
                    </div>

                    {/* Información de la próxima jornada */}
                    {paquete.jornadas_info.jornada_mas_cercana && (
                      <div className="proxima-jornada">
                        <h5>📅 Próxima jornada:</h5>
                        <p>
                          <strong>Jornada {paquete.jornadas_info.jornada_mas_cercana.numero}</strong> - {' '}
                          {formatFechaJornada(paquete.jornadas_info.jornada_mas_cercana.fecha)}
                        </p>
                      </div>
                    )}

                    {/* Sección de equipación */}
                    <div className="equipacion-section" style={{
                      backgroundColor: '#f8f9fa',
                      borderRadius: '12px',
                      padding: isMobile ? '16px' : '20px',
                      border: '1px solid #e1e5e9',
                      marginBottom: '20px',
                    }}>
                      <div className="equipacion-header" style={{
                        marginBottom: '16px',
                      }}>
                        <h4 style={{
                          fontSize: isMobile ? '1.1rem' : '1.2rem',
                          fontWeight: '600',
                          color: '#061156',
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}>
                          {/* Título de equipación según tipo de paquete */}
                          {paquete.tipo === 'primera_experiencia' ? (
                            <>👕 Equipación prestada</>
                          ) : (paquete.id === 9 || paquete.id === 10) ? (
                            <>👕 {equipacionInfo?.nombre || 'Equipación Completa JOMA'}</>
                          ) : futbolistasNuevos?.[paquete.id]?.primera_experiencia_usada ? (
                            <>
                              👕 {equipacionInfo?.nombre || 'Equipación Completa JOMA'}{' '}
                              <span style={{ textDecoration: 'line-through', color: '#aaa', fontSize: '0.85em' }}>+{equipacionInfo?.precio || 15}€</span>{' '}
                              <span style={{ color: '#28a745', fontSize: '0.85em' }}>+{Math.max(0, (equipacionInfo?.precio || 15) - 3)}€</span>
                              <span style={{ backgroundColor: '#E3FF38', color: '#000', fontSize: '10px', fontWeight: '700', padding: '1px 6px', borderRadius: '2px', marginLeft: '4px', verticalAlign: 'middle' }}>-3€</span>
                            </>
                          ) : (
                            <>👕 {equipacionInfo?.nombre || 'Equipación Completa JOMA'} (+{equipacionInfo?.precio || 15}€)</>
                          )}
                        </h4>
                        <p className="equipacion-description" style={{
                          fontSize: isMobile ? '13px' : '14px',
                          color: '#6c757d',
                          margin: '0',
                          lineHeight: '1.4',
                        }}>
                          {paquete.tipo === 'primera_experiencia'
                            ? 'Recibirás una equipación oficial al llegar. Deberás devolverla al finalizar el partido.'
                            : (equipacionInfo?.descripcion || 'Incluye Camiseta y Pantalón Oficial de la Liga Prokids')}
                        </p>
                      </div>

                      <div className="equipacion-options">
                        {/* Primera Experiencia: equipación prestada, no se compra */}
                        {paquete.tipo === 'primera_experiencia' ? (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: isMobile ? '12px 0' : '8px 0',
                          }}>
                            <span style={{ fontSize: '20px' }}>👕</span>
                            <span style={{
                              fontSize: isMobile ? '15px' : '14px',
                              fontWeight: '600',
                              color: '#495057',
                            }}>
                              Equipación prestada — se te entregará al llegar al campo y deberás devolverla al finalizar
                            </span>
                          </div>
                        ) : /* Para paquetes Black Friday (ID 9) y Reyes Magos (ID 10): equipación incluida, sin checkbox */
                        (paquete.id === 9 || paquete.id === 10) ? (
                          <>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: isMobile ? '12px 0' : '8px 0',
                              marginBottom: '12px',
                            }}>
                              <FaCheckCircle style={{ color: '#4CAF50', fontSize: '20px' }} />
                              <span style={{
                                fontSize: isMobile ? '15px' : '14px',
                                fontWeight: '600',
                                color: '#4CAF50',
                              }}>
                                ✨ Equipación JOMA Incluida
                              </span>
                            </div>

                            {/* Selector de talla (OBLIGATORIO para Black Friday y Reyes Magos) */}
                            <div className="talla-selector" style={{
                              padding: isMobile ? '16px' : '12px',
                              backgroundColor: '#ffffff',
                              borderRadius: '8px',
                              border: obligatoria ? '2px solid #4CAF50' : '1px solid #e1e5e9',
                            }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '8px',
                              }}>
                                <label style={{
                                  fontSize: isMobile ? '15px' : '14px',
                                  fontWeight: '600',
                                  color: '#495057',
                                  margin: 0,
                                }}>
                                  Talla de Equipación {obligatoria && <span style={{ color: '#FF5722' }}>*</span>}
                                </label>
                                <button
                                  type="button"
                                  onClick={() => setMostrarGuiaTallas(true)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#4352A9',
                                    fontSize: isMobile ? '13px' : '12px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    padding: '0',
                                  }}
                                >
                                  📏 Ver Guía de Tallas
                                </button>
                              </div>
                              <select
                                value={tallaEquipacion[paquete.id] || ''}
                                onChange={(e) => handleTallaChange(paquete.id, e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: isMobile ? '12px 16px' : '8px 12px',
                                  fontSize: isMobile ? '16px' : '14px',
                                  border: '2px solid #e1e5e9',
                                  borderRadius: '6px',
                                  backgroundColor: '#ffffff',
                                  color: '#495057',
                                  minHeight: isMobile ? '44px' : '36px',
                                  cursor: 'pointer',
                                  transition: 'border-color 0.3s ease',
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#4352A9'}
                                onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
                              >
                                <option value="">Seleccionar talla</option>
                                {tallasDisponiblesCalculadas.map(talla => (
                                  <option key={talla} value={talla}>
                                    {talla}
                                  </option>
                                ))}
                              </select>
                              {obligatoria && (
                                <small style={{
                                  display: 'block',
                                  marginTop: '6px',
                                  fontSize: isMobile ? '12px' : '11px',
                                  color: '#6c757d',
                                }}>
                                  La equipación incluye camiseta y pantalón oficial JOMA
                                </small>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="d-flex flex-column gap-3">
                            <div className="equipacion-toggle">
                              <label className="d-flex align-items-center mb-0 p-2 rounded" style={{
                                cursor: 'pointer',
                                backgroundColor: '#ffffff',
                                border: '1px solid #e1e5e9',
                                transition: 'all 0.2s ease'
                              }}>
                                <div className="form-check form-switch mb-0" style={{ paddingLeft: '2.5em' }}>
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id={`equipacion-${paquete.id}`}
                                    checked={incluirEquipacion[paquete.id] || false}
                                    onChange={(e) => handleEquipacionChange(paquete.id, e.target.checked)}
                                    disabled={obligatoria}
                                    style={{
                                      cursor: obligatoria ? 'not-allowed' : 'pointer',
                                      width: '40px',
                                      height: '20px'
                                    }}
                                  />
                                  <span className="fw-medium text-dark">
                                    Incluir Equipación Oficial
                                    {obligatoria && (
                                      <span className="badge bg-danger ms-2">Obligatoria</span>
                                    )}
                                  </span>
                                </div>
                              </label>
                            </div>

                            {incluirEquipacion[paquete.id] && (
                              <div className="talla-selector" style={{
                                marginTop: '16px',
                                padding: isMobile ? '16px' : '12px',
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                border: obligatoria ? '2px solid #4CAF50' : '1px solid #e1e5e9',
                              }}>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom: '8px',
                                }}>
                                  <label style={{
                                    fontSize: isMobile ? '15px' : '14px',
                                    fontWeight: '600',
                                    color: '#495057',
                                    margin: 0,
                                  }}>
                                    Talla de Equipación {obligatoria && <span style={{ color: '#FF5722' }}>*</span>}
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => setMostrarGuiaTallas(true)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#4352A9',
                                      fontSize: isMobile ? '13px' : '12px',
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      padding: '0',
                                    }}
                                  >
                                    📏 Ver Guía de Tallas
                                  </button>
                                </div>
                                <select
                                  value={tallaEquipacion[paquete.id] || ''}
                                  onChange={(e) => handleTallaChange(paquete.id, e.target.value)}
                                  style={{
                                    width: '100%',
                                    padding: isMobile ? '12px 16px' : '8px 12px',
                                    fontSize: isMobile ? '16px' : '14px',
                                    border: '2px solid #e1e5e9',
                                    borderRadius: '6px',
                                    backgroundColor: '#ffffff',
                                    color: '#495057',
                                    minHeight: isMobile ? '44px' : '36px',
                                    cursor: 'pointer',
                                    transition: 'border-color 0.3s ease',
                                  }}
                                  onFocus={(e) => e.target.style.borderColor = '#4352A9'}
                                  onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
                                >
                                  <option value="">Seleccionar talla</option>
                                  {tallasDisponiblesCalculadas.map(talla => (
                                    <option key={talla} value={talla}>
                                      {talla}
                                    </option>
                                  ))}
                                </select>
                                {obligatoria && (
                                  <small style={{
                                    display: 'block',
                                    marginTop: '6px',
                                    fontSize: isMobile ? '12px' : '11px',
                                    color: '#6c757d',
                                  }}>
                                    La equipación incluye camiseta y pantalón oficial JOMA
                                  </small>
                                )}
                              </div>
                            )}

                            {obligatoria && (
                              <div className="alert alert-warning mt-3 mb-0 py-2 d-flex align-items-center">
                                <i className="material-icons text-warning me-2" style={{ fontSize: '18px' }}>info</i>
                                <small>
                                  La equipación oficial es obligatoria para este torneo y sede.
                                </small>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ═══ SEGURO VOLUNTARIO DE ACCIDENTES ═══ */}
                  {selectedFutbolista && seguroInfo && !futbolistasTienenSeguro[selectedFutbolista] && (
                    <div style={{
                      padding: isMobile ? '14px 16px' : '16px 20px',
                      borderTop: '1px solid #e9ecef',
                      backgroundColor: '#f0f7ff'
                    }}>
                      <div className="seguro-toggle">
                        <label className="d-flex align-items-center mb-0 p-2 rounded" style={{
                          cursor: 'pointer',
                          backgroundColor: '#ffffff',
                          border: incluirSeguro[paquete.id] ? '2px solid #2196F3' : '1px solid #e1e5e9',
                          transition: 'all 0.2s ease'
                        }}>
                          <div className="form-check form-switch mb-0" style={{ paddingLeft: '2.5em' }}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id={`seguro-${paquete.id}`}
                              checked={incluirSeguro[paquete.id] || false}
                              onChange={(e) => handleSeguroChange(paquete.id, e.target.checked)}
                              style={{
                                cursor: 'pointer',
                                width: '40px',
                                height: '20px'
                              }}
                            />
                            <span className="fw-medium text-dark">
                              🛡️ Incluir Seguro de Accidentes
                              <span className="badge bg-info ms-2">Voluntario</span>
                            </span>
                          </div>
                        </label>
                      </div>

                      {incluirSeguro[paquete.id] && (
                        <div style={{
                          marginTop: '10px',
                          padding: '12px',
                          backgroundColor: '#e3f2fd',
                          borderRadius: '8px',
                          border: '1px solid #bbdefb',
                          fontSize: isMobile ? '13px' : '12px',
                          color: '#1565c0'
                        }}>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                            Seguro Voluntario de Accidentes Deportivos
                          </div>
                          <div>
                            📅 Cobertura: {new Date(seguroInfo.fecha_inicio).toLocaleDateString('es-ES')} - {new Date(seguroInfo.fecha_fin).toLocaleDateString('es-ES')}
                          </div>
                          <div style={{ marginTop: '4px' }}>
                            💰 Precio: <strong>{formatPrice(seguroInfo.precio_prorrateado)}</strong>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedFutbolista && futbolistasTienenSeguro[selectedFutbolista] && (
                    <div style={{
                      padding: isMobile ? '10px 16px' : '12px 20px',
                      borderTop: '1px solid #e9ecef',
                      backgroundColor: '#e8f5e9'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: isMobile ? '13px' : '12px',
                        color: '#2e7d32'
                      }}>
                        <FaCheckCircle />
                        <span>Este futbolista ya tiene el Seguro de Accidentes activo ✅</span>
                      </div>
                    </div>
                  )}

                  {/* ═══ SELECCIÓN DE JORNADAS ═══
                      Permite al tutor elegir en qué jornadas inscribirse.
                      Si no activa la selección manual, se asignan las N próximas automáticamente.
                  */}
                  {paquete.jornadas_info?.jornadas_disponibles?.length > 0 && (
                    <div style={{
                      padding: isMobile ? '14px 16px' : '16px 20px',
                      borderTop: '1px solid #e9ecef',
                      backgroundColor: '#f8f9fa'
                    }}>
                      {/* Toggle automático / manual */}
                      <div
                        onClick={() => {
                          const newVal = !seleccionManual[paquete.id];
                          setSeleccionManual(prev => ({ ...prev, [paquete.id]: newVal }));
                          if (!newVal) setJornadasSeleccionadas(prev => ({ ...prev, [paquete.id]: [] }));
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          backgroundColor: seleccionManual[paquete.id] ? '#4352A9' : '#e9ecef',
                          color: seleccionManual[paquete.id] ? '#fff' : '#555',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <span style={{ fontSize: '13px', fontWeight: '500' }}>
                          📅 {seleccionManual[paquete.id] ? 'Selección manual de fechas' : 'Fechas automáticas (próximas disponibles)'}
                        </span>
                        <span style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          backgroundColor: seleccionManual[paquete.id] ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)'
                        }}>
                          {seleccionManual[paquete.id] ? 'Cambiar a automático' : 'Elegir fechas'}
                        </span>
                      </div>

                      {/* Lista de jornadas seleccionables */}
                      {seleccionManual[paquete.id] && (
                        <div style={{ marginTop: '10px' }}>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                            gap: '6px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            padding: '2px'
                          }}>
                            {paquete.jornadas_info.jornadas_disponibles.map((jornada) => {
                              const selected = jornadasSeleccionadas[paquete.id] || [];
                              const isChecked = selected.includes(jornada.id);
                              const yaInscrito = jornadasYaInscritas.includes(jornada.id);
                              const limitReached = selected.length >= paquete.cantidad_jornadas && !isChecked;
                              const disabled = yaInscrito || limitReached;
                              const fechaCorta = formatFechaJornadaCorta(jornada.fecha);

                              return (
                                <div
                                  key={jornada.id}
                                  onClick={() => {
                                    if (disabled) return;
                                    setJornadasSeleccionadas(prev => {
                                      const current = prev[paquete.id] || [];
                                      const updated = isChecked
                                        ? current.filter(id => id !== jornada.id)
                                        : [...current, jornada.id];
                                      return { ...prev, [paquete.id]: updated };
                                    });
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 10px',
                                    borderRadius: '8px',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    opacity: disabled ? 0.4 : 1,
                                    backgroundColor: yaInscrito ? '#e8f5e9' : isChecked ? '#e8edf7' : '#fff',
                                    border: yaInscrito ? '2px solid #81c784' : isChecked ? '2px solid #4352A9' : '2px solid #e9ecef',
                                    transition: 'all 0.15s ease',
                                    fontSize: '13px'
                                  }}
                                >
                                  <div style={{
                                    width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0,
                                    backgroundColor: yaInscrito ? '#81c784' : isChecked ? '#4352A9' : '#fff',
                                    border: yaInscrito ? 'none' : isChecked ? 'none' : '2px solid #ccc',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#fff', fontSize: '11px', fontWeight: 'bold'
                                  }}>
                                    {(yaInscrito || isChecked) && '✓'}
                                  </div>
                                  <div style={{ lineHeight: '1.2' }}>
                                    <span style={{ fontWeight: '600', color: yaInscrito ? '#388e3c' : '#333' }}>J{jornada.numero}</span>
                                    <span style={{ color: yaInscrito ? '#388e3c' : '#666', marginLeft: '6px', textTransform: 'capitalize' }}>
                                      {yaInscrito ? 'Ya inscrito' : fechaCorta}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Contador */}
                          <div style={{
                            marginTop: '8px',
                            fontSize: '12px',
                            fontWeight: '600',
                            textAlign: 'center',
                            color: (jornadasSeleccionadas[paquete.id]?.length || 0) === paquete.cantidad_jornadas ? '#28a745' : '#6c757d'
                          }}>
                            {(jornadasSeleccionadas[paquete.id]?.length || 0) === paquete.cantidad_jornadas
                              ? `✅ ${paquete.cantidad_jornadas} jornada${paquete.cantidad_jornadas > 1 ? 's' : ''} seleccionada${paquete.cantidad_jornadas > 1 ? 's' : ''}`
                              : `${jornadasSeleccionadas[paquete.id]?.length || 0} de ${paquete.cantidad_jornadas} seleccionadas`
                            }
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                <div className="paquete-footer" style={{
                  padding: isMobile ? '20px' : '24px',
                  borderTop: '1px solid #e1e5e9',
                  backgroundColor: '#ffffff',
                }}>
                  <button
                    onClick={() => handleInscripcion(paquete)}
                    disabled={
                      validarInscripcion(paquete) !== null ||
                      inscribiendo
                    }
                    style={{
                      width: '100%',
                      padding: isMobile ? '16px 24px' : '12px 20px',
                      fontSize: isMobile ? '16px' : '14px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: (validarInscripcion(paquete) !== null || inscribiendo) ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      minHeight: isMobile ? '48px' : '40px',
                      backgroundColor: (validarInscripcion(paquete) !== null || inscribiendo) ? '#6c757d' : '#4352A9',
                      color: '#ffffff',
                      opacity: (validarInscripcion(paquete) !== null || inscribiendo) ? 0.7 : 1,
                    }}
                    onMouseOver={(e) => {
                      if (!isMobile && !e.target.disabled) {
                        e.target.style.backgroundColor = '#061156';
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isMobile && !e.target.disabled) {
                        e.target.style.backgroundColor = '#4352A9';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }
                    }}
                    onTouchStart={(e) => {
                      if (isMobile && !e.target.disabled) {
                        e.target.style.transform = 'scale(0.98)';
                      }
                    }}
                    onTouchEnd={(e) => {
                      if (isMobile && !e.target.disabled) {
                        setTimeout(() => {
                          e.target.style.transform = 'scale(1)';
                        }, 150);
                      }
                    }}
                  >
                    {inscribiendo && paqueteSeleccionado?.id === paquete.id ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid transparent',
                          borderTop: '2px solid currentColor',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                        }} />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <FaShoppingCart style={{ fontSize: '16px' }} />
                        {validarInscripcion(paquete) || 'Inscribir Futbolista'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>

      {/* ═══ COMPRA STANDALONE DE SEGURO ═══ */}
      {selectedFutbolista && seguroInfo && !futbolistasTienenSeguro[selectedFutbolista] && (
        <div style={{
          maxWidth: '1200px',
          margin: '30px auto 0',
          padding: isMobile ? '0 16px' : '0 20px',
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            border: '2px solid #2196F3',
            overflow: 'hidden',
          }}>
            <div style={{
              padding: isMobile ? '20px' : '24px',
              background: 'linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)',
              color: '#ffffff',
            }}>
              <h3 style={{
                fontSize: isMobile ? '1.2rem' : '1.3rem',
                fontWeight: '700',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}>
                🛡️ Seguro Voluntario de Accidentes
                <span style={{
                  fontSize: '12px',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  padding: '2px 10px',
                  borderRadius: '12px',
                  fontWeight: '500',
                }}>
                  Compra independiente
                </span>
              </h3>
              <p style={{
                margin: '8px 0 0',
                fontSize: isMobile ? '13px' : '14px',
                opacity: 0.9,
              }}>
                ¿Ya tienes paquete? Añade el seguro de accidentes deportivos para tu futbolista
              </p>
            </div>

            <div style={{
              padding: isMobile ? '20px' : '24px',
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '16px',
                marginBottom: '16px',
              }}>
                <div>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>📅 Cobertura</div>
                  <div style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>
                    {new Date(seguroInfo.fecha_inicio).toLocaleDateString('es-ES')} - {new Date(seguroInfo.fecha_fin).toLocaleDateString('es-ES')}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>💰 Precio</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1565c0' }}>
                      {formatPrice(seguroInfo.precio_prorrateado)}
                    </span>
                    {seguroInfo.precio_prorrateado < seguroInfo.precio_base && (
                      <span style={{
                        textDecoration: 'line-through',
                        color: '#999',
                        fontSize: '14px',
                      }}>
                        {formatPrice(seguroInfo.precio_base)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div style={{
                padding: '10px 14px',
                backgroundColor: '#e3f2fd',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#1565c0',
                marginBottom: '16px',
              }}>
                ℹ️ Precio único para toda la temporada, independientemente de la fecha de alta.
                Cubre accidentes deportivos durante todas las actividades de Liga ProKids.
              </div>

              <button
                onClick={() => {
                  const futbolistaCompleto = futbolistas.find(f => f.id.toString() === selectedFutbolista.toString());
                  navigate('/seguro/checkout', {
                    state: {
                      type: 'seguro_standalone',
                      futbolista_id: selectedFutbolista,
                      futbolista: futbolistaCompleto,
                      seguro: {
                        precio_prorrateado: seguroInfo.precio_prorrateado,
                        precio_base: seguroInfo.precio_base,
                        fecha_inicio: seguroInfo.fecha_inicio,
                        fecha_fin: seguroInfo.fecha_fin,
                      }
                    }
                  });
                }}
                style={{
                  width: '100%',
                  padding: isMobile ? '14px' : '12px',
                  backgroundColor: '#1565c0',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: isMobile ? '16px' : '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'background-color 0.3s ease',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#0d47a1'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#1565c0'}
              >
                <FaShoppingCart style={{ fontSize: '16px' }} />
                Comprar Seguro - {formatPrice(seguroInfo.precio_prorrateado)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Guía de Tallas */}
      {mostrarGuiaTallas && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            padding: isMobile ? '20px' : '40px',
          }}
          onClick={() => setMostrarGuiaTallas(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              maxWidth: '900px',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Botón de cerrar */}
            <button
              onClick={() => setMostrarGuiaTallas(false)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: '#FF5722',
                color: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                fontSize: '24px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                zIndex: 10,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              }}
            >
              ×
            </button>

            {/* Título */}
            <div style={{
              padding: isMobile ? '20px' : '30px',
              borderBottom: '2px solid #e1e5e9',
            }}>
              <h2 style={{
                margin: 0,
                fontSize: isMobile ? '20px' : '24px',
                fontWeight: '700',
                color: '#2c3e50',
                textAlign: 'center',
              }}>
                📏 Guía de Tallas JOMA
              </h2>
            </div>

            {/* Imagen de la guía */}
            <div style={{
              padding: isMobile ? '20px' : '30px',
              textAlign: 'center',
            }}>
              <img
                src="/images/tallas_Joma.png"
                alt="Guía de Tallas JOMA"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                }}
              />
            </div>

            {/* Botón de cerrar inferior */}
            <div style={{
              padding: isMobile ? '15px 20px' : '20px 30px',
              borderTop: '2px solid #e1e5e9',
              textAlign: 'center',
            }}>
              <button
                onClick={() => setMostrarGuiaTallas(false)}
                style={{
                  backgroundColor: '#4352A9',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: isMobile ? '12px 30px' : '10px 25px',
                  fontSize: isMobile ? '16px' : '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#364291'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#4352A9'}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InscripcionJornadas;
